import { SectionTitle } from '@elotech/components';
import { ArquivoComponent } from 'common/components';
import { ArquivoService } from 'common/service';
import { CrudRoleBase, FilePathEnum } from 'common/type/enum';
import { ReactNode, RefAttributes, forwardRef, useMemo } from 'react';

import { useCrudRole } from '../../hooks';
import { ModulosUrlEnum, isCurrentModulo } from '../../hooks/useModuloAtual';
import { ArquivoEntity } from '../../type';
import { ArquivoComponentRef, ArquivoEntityWithTemp } from './ArquivoComponent';

export type ArquivoRef = ArquivoComponentRef;

type BaseProps = {
  id?: string;
  title?: string;
  baseUrl: string;
  byTipo?: string;
  resource: string;
  loading?: boolean;
  canUpload?: boolean;
  canDelete?: boolean | ((file: ArquivoEntity) => boolean);
  showTitle?: boolean;
  filePath: FilePathEnum;
  maxFileNameLength?: number;
  maxFileCount?: number;
  onVisualizarArquivoHtml?: (
    template: string,
    fileName: string
  ) => Promise<string | undefined | void>;
  extraButton?: (file: ArquivoEntity) => any;
  labels?: (file: ArquivoEntity) => any;
  showArquivoProps?: boolean;
  children?: ReactNode;
  getExtraCaption?(file: ArquivoEntity): any;
  afterSave?: (arquivoEntity: ArquivoEntity) => void;
  afterDelete?: () => void;
  onFinish?: () => void;
  acceptAll?: boolean;
  enforceFileSizeLimit?: boolean;
};

type PropsRole<ROLE extends string | undefined = undefined> =
  ROLE extends string
    ? { crudRole: CrudRoleBase<ROLE> }
    : { crudRole?: undefined };

type Props<Role extends string | undefined = undefined> = BaseProps &
  PropsRole<Role>;

type ArquivoType = <ROLE extends string | undefined = undefined>(
  props: Props<ROLE> & RefAttributes<ArquivoComponentRef>
) => React.ReactElement | null;

type RoleOrEmpty<R> = R extends undefined ? '' : R;

const Arquivo: ArquivoType = forwardRef(
  <ROLE extends string | undefined = undefined>(
    props: Props<ROLE>,
    ref: React.Ref<ArquivoRef>
  ) => {
    const {
      baseUrl,
      loading,
      resource,
      canUpload = true,
      canDelete = true,
      showTitle = true,
      title = 'Arquivos',
      extraButton,
      labels,
      afterSave,
      afterDelete,
      onFinish,
      crudRole,
      showArquivoProps,
      acceptAll = false,
      enforceFileSizeLimit = true,
      ...rest
    } = props;

    const crudPermissions = useCrudRole<RoleOrEmpty<ROLE>>(
      crudRole ?? undefined
    );
    const hasWritePermission = crudPermissions.update || crudPermissions.create;
    const canDeleteResolved:
      | boolean
      | ((file: ArquivoEntityWithTemp) => boolean) = !crudPermissions.delete
      ? false
      : typeof canDelete === 'function'
      ? (file: ArquivoEntityWithTemp) =>
          (canDelete as (file: ArquivoEntity) => boolean)(file as ArquivoEntity)
      : canDelete;

    const service = useMemo(
      () => new ArquivoService(baseUrl, resource),
      [baseUrl, resource]
    );

    if (!crudPermissions.read) {
      return null;
    }

    const isCompras = isCurrentModulo(ModulosUrlEnum.COMPRAS);

    return (
      <>
        {showTitle && <SectionTitle>{title}</SectionTitle>}
        <ArquivoComponent
          service={service}
          isLoading={loading}
          canUpload={crudPermissions.create && canUpload}
          canDelete={canDeleteResolved}
          extraButton={hasWritePermission ? extraButton : undefined}
          showArquivoProps={hasWritePermission && showArquivoProps}
          labels={labels}
          maxFileCount={props.maxFileCount}
          ref={ref}
          afterSave={afterSave}
          onFinish={onFinish}
          afterDelete={afterDelete}
          acceptAll={isCompras ? true : acceptAll}
          enforceFileSizeLimit={isCompras ? false : enforceFileSizeLimit}
          {...rest}
        />
      </>
    );
  }
);

export default Arquivo;
